/**
 * Public Registration Page — scope-agnostic RSVP.
 * URL: /register?scope_type=...&scope_id=...&scope_label=...
 * Anyone with the link can self-register. Returns a QR code to save.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Loader2, QrCode } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicScopeRegister() {
  const [params] = useSearchParams();
  const scopeType = params.get('scope_type') || 'event';
  const scopeId = params.get('scope_id') || '';
  const scopeLabel = params.get('scope_label') || '';

  const [form, setForm] = useState({ full_name: '', email: '', phone: '', category: 'attendee' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/scope-reg/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, scope_type: scopeType, scope_id: scopeId, scope_label: scopeLabel }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Registration failed');
      setResult(await r.json());
    } catch (err) {
      setError(err.message);
    } finally { setSubmitting(false); }
  };

  if (!scopeId) {
    return <div className="min-h-screen flex items-center justify-center p-6 text-sm text-slate-500">Invalid link. Missing scope_id.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-cyan-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md" data-testid="public-register-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-indigo-500" />
            {scopeLabel || `Register for ${scopeType}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {result ? (
            <div className="space-y-3 text-center py-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
              <p className="font-semibold text-lg">You're in!</p>
              <p className="text-sm text-slate-600">Save this QR — show it at check-in.</p>
              <div className="p-4 bg-slate-100 rounded-lg font-mono text-lg break-all" data-testid="public-qr-result">{result.qr_code}</div>
              {result.already_registered && <p className="text-xs text-amber-600">(already registered — reusing your existing QR)</p>}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div><Label className="text-xs">Full name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="public-name" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="public-email" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div>
                <Label className="text-xs">I am a…</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attendee">Attendee</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                    <SelectItem value="volunteer">Volunteer</SelectItem>
                    <SelectItem value="guest">Guest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full" data-testid="public-register-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Register
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
